import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { handleUnauthorized } from "../../utils/apiHelper";
import moment from "moment";
import Swal from "sweetalert2";
import { Link, useLocation } from "react-router-dom";


const UserActivityListPage = ({ userId }) => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedItems, setSelectedItems] = useState([]);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const navigate = useNavigate();
    const authToken = localStorage.getItem("authToken");

    // Sorting
    const [sortColumn, setSortColumn] = useState("timestamp");
    const [sortOrder, setSortOrder] = useState("desc");

    useEffect(() => {
        setCurrentPage(1); // Reset page on search/itemsPerPage change
    }, [searchQuery, itemsPerPage]);

    useEffect(() => {
        fetchData(currentPage);
    }, [currentPage, searchQuery, itemsPerPage, sortColumn, sortOrder]);

    const fetchData = async (page) => {
        const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/activityLog/${userId}?page=${page}&limit=${itemsPerPage}&sort=${sortColumn}&order=${sortOrder}&search=${searchQuery}`;
        try {
            setLoading(true);
            const response = await fetch(API_ENDPOINT, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            if (response.status === 401) {
                handleUnauthorized(navigate);
                return;
            }
            if (!response.ok) throw new Error("Failed to fetch data");
            const data = await response.json();
            setActivities(data.data.logs || []);
            setTotalItems(data.data.pagination.total || 0);
            setTotalPages(data.data.pagination.totalPages || 1);
        } catch (error) {
            setActivities([]);
            setErrorMessage(error.message);
            setTimeout(() => setErrorMessage(""), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("asc");
        }
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedItems(activities.map((item) => item._id));
        } else {
            setSelectedItems([]);
        }
    };

    const handleSelectItem = (id) => {
        setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const isSelected = (id) => selectedItems.includes(id);

    const handleNext = () => currentPage < totalPages && setCurrentPage((p) => p + 1);
    const handlePrev = () => currentPage > 1 && setCurrentPage((p) => p - 1);

    const getPaginationRange = () => {
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + 4);
        if (end - start < 4) start = Math.max(1, end - 4);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    };
    const handleViewDetails = (details) => {
        Swal.fire({
            title: "Activity Details",
            html: `<pre style="text-align:left; max-height:300px; overflow:auto; background:#f6f7fc; padding:10px; border-radius:10px;">${JSON.stringify(
                details,
                null,
                2
            )}</pre>`,
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: false,
            confirmButtonText: "Copy JSON",
            cancelButtonText: "Close",
            preConfirm: () => {
                navigator.clipboard.writeText(JSON.stringify(details, null, 2));
                Swal.fire("Copied!", "Details copied to clipboard.", "success");
            },
            width: "600px",
        });
    };

    const formatAction = (action) => {
        return action
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    // Format module: "vault" => "Vault"
    const formatModule = (module) => {
        return module.charAt(0).toUpperCase() + module.slice(1);
    };

    return (
        <div className="card h-100 p-0 radius-12">
            {successMessage && <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />}
            {errorMessage && <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />}

            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">Show</span>

                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6"
                        style={{
                            fontSize: "16px",
                            borderRadius: "7px",
                            backgroundPositionX: "unset",
                            backgroundPosition: "right 0.75rem center",
                        }}
                        value={itemsPerPage}
                        defaultValue="Select Number"
                        onChange={handleItemsPerPageChange}
                    >
                        <option value="10">10 items per page</option>
                        <option value="20">20 items per page</option>
                        <option value="50">50 items per page</option>
                        <option value="100">100 items per page</option>
                    </select>
                    {/* <input
                        type="text"
                        placeholder="Search"
                        className="bg-base h-40-px"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    /> */}
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th>
                                    <input type="checkbox" onChange={handleSelectAll} checked={selectedItems.length === activities.length && activities.length > 0} />
                                </th>
                                <th>Module</th>
                                <th onClick={() => handleSort("action")}>Action</th>
                                <th>IP</th>
                                <th>User Agent</th>
                                <th>Date</th>
                                <th>Details</th>

                            </tr>


                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="text-center">
                                        <span className="loader-15"></span>
                                    </td>
                                </tr>
                            ) : activities.length > 0 ? (
                                activities.map((item) => (
                                    <tr key={item._id}>
                                        <td>
                                            <input type="checkbox" checked={isSelected(item._id)} onChange={() => handleSelectItem(item._id)} />
                                        </td>
                                    
                                        <td>{formatModule(item.module)}</td>
                                        <td>{formatAction(item.action)}</td>
                                        <td>{item.ip || "-"}</td>
                                        <td className="wd-ua" title={item.userAgent}>
                                            {item.userAgent || "-"}
                                        </td>

                                        <td>{moment(item.timestamp).format("DD-MM-YYYY, hh:mm A")}</td>
                                        <td>
                                            <button
                                                title="View"
                                                onClick={() => handleViewDetails(item.details)}
                                                className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                            >
                                                <Icon icon="majesticons:eye-line" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center">
                                        No Data Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                    <span>
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                    </span>
                    <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <Link
                                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#"
                                onClick={handlePrev}
                            >
                                <Icon icon="ep:d-arrow-left" />
                            </Link>
                        </li>
                        {getPaginationRange().map((page) => {
                            // Return JSX for pagination items
                            return (
                                <li key={page} className="page-item">
                                    <Link
                                        className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                                            ? "bg-primary-600 text-white"
                                            : "bg-neutral-200"
                                            }`}
                                        to="#"
                                        onClick={() => handlePageChange(page)}
                                    >
                                        {page}
                                    </Link>
                                </li>
                            );
                        })}

                        <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""
                                }`}
                        >
                            <Link
                                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                to="#"
                                onClick={handleNext}
                            >
                                <Icon icon="ep:d-arrow-right" />
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default UserActivityListPage;
